/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 페이지네이션 클래스를 정의한다.
 *
 * @file /src/components/pagination/Pagination.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 21.
 */
import * as parent from '@components/toolbar/Toolbar';
import { Button } from '@components/button/Button';
import { Display } from '@components/form/field/display/Display';
import { Number } from '@components/form/field/number/Number';
import { Format } from '@utils/Format';
import { Language } from '@utils/Language';
import { Stores } from '@models/store/Store';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 데이터스토어
     */
    store?: Stores;

    /**
     * 선택방법
     */
    type?: 'default' | 'simple';

    /**
     * reload 여부
     */
    isReload?: boolean;
}

export class Pagination extends parent.Toolbar {
    /**
     * 컴포넌트 타입
     */
    type = 'toolbar';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'pagination';

    /**
     * 데이터스토어
     */
    store: Stores = null;

    /**
     * 선택방법
     */
    paginationType: 'default' | 'simple';

    /**
     * reload 여부
     */
    isReload: boolean = true;

    /**
     * 맨 처음으로 이동하는 버튼
     */
    firstButton: Button = null;

    /**
     * 이전으로 이동하는 버튼
     */
    prevButton: Button = null;

    /**
     * 다음으로 이동하는 버튼
     */
    nextButton: Button = null;

    /**
     * 맨 마지막으로 이동하는 버튼
     */
    lastButton: Button = null;

    /**
     * 리로드 버튼
     */
    reloadButton: Button = null;

    /**
     * 페이지 입력 필드
     */
    pageInput: Number = null;

    /**
     * 마지막 페이지 뷰 필드
     */
    pageDisplay: Display = null;

    /**
     * 페이지네이션 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Pagination> = null) {
        super(properties);

        this.setStore(properties?.store ?? null);
        this.paginationType = properties?.type ?? 'default';
        this.isReload = properties?.isReload !== false;
    }

    /**
     * 페이지네이션 컴포넌트를 초기화한다.
     */
    initItems(): void {
        this.properties.tools = [...(this.properties.tools ?? [])];

        const tools = [];

        tools.push(this.getFirstButton());
        tools.push(this.getPrevButton());
        if (this.paginationType == 'default') {
            tools.push('-');
        }
        tools.push(this.getPageInput());
        tools.push(this.getPageDisplay());

        if (this.paginationType == 'default') {
            tools.push('-');
        }

        tools.push(this.getNextButton());
        tools.push(this.getLastButton());

        if (this.isReload === true) {
            tools.push('-');
            tools.push(this.getReloadButton());
        }

        this.properties.tools.unshift(...tools);
        super.initItems();
    }

    /**
     * 그리드패널을 가져온다.
     *
     * @returns 그리드패널
     */
    setStore(store: Stores): void {
        this.store = store;
        if (store !== null) {
            this.store.addEvent('load', () => {
                this.#update();
            });

            if (this.store.isLoaded() == true) {
                this.#update();
            }
        }
    }

    /**
     * 부모 패널에 정의된 스토어가 있다면 가져온다.
     *
     * @returns 부모 패널에 정의된 스토어
     */
    getStore(): Stores {
        return this.store;
    }

    /**
     * 맨 처음으로 이동하는 버튼을 가져온다.
     *
     * @returns 맨 처음으로 이동하는 버튼
     */
    getFirstButton(): Button {
        if (this.firstButton === null) {
            this.firstButton = new Button({
                icon: 'mi mi-step-backward',
                hidden: this.paginationType == 'simple',
                disabled: true,
                handler: async () => {
                    await this.movePage('FIRST');
                },
            });
        }

        return this.firstButton;
    }

    /**
     * 이전으로 이동하는 버튼을 가져온다.
     *
     * @returns 이전으로 이동하는 버튼
     */
    getPrevButton(): Button {
        if (this.prevButton === null) {
            this.prevButton = new Button({
                icon: 'mi mi-angle-left',
                disabled: true,
                handler: async () => {
                    await this.movePage('PREV');
                },
            });
        }

        return this.prevButton;
    }

    /**
     * 다음으로 이동하는 버튼을 가져온다.
     *
     * @returns 다음으로 이동하는 버튼
     */
    getNextButton(): Button {
        if (this.nextButton === null) {
            this.nextButton = new Button({
                icon: 'mi mi-angle-right',
                disabled: true,
                handler: async () => {
                    await this.movePage('NEXT');
                },
            });
        }

        return this.nextButton;
    }

    /**
     * 맨 마지막으로 이동하는 버튼을 가져온다.
     *
     * @returns 맨 마지막으로 이동하는 버튼
     */
    getLastButton(): Button {
        if (this.lastButton === null) {
            this.lastButton = new Button({
                icon: 'mi mi-step-forward',
                hidden: this.paginationType == 'simple',
                disabled: true,
                handler: async () => {
                    await this.movePage('LAST');
                },
            });
        }

        return this.lastButton;
    }

    /**
     * 리로드 버튼을 가져온다.
     *
     * @returns 리로드 버튼
     */
    getReloadButton(): Button {
        if (this.reloadButton === null) {
            this.reloadButton = new Button({
                icon: 'mi mi-refresh',
                handler: async () => {
                    await this.getStore().reload();
                },
            });
        }

        return this.reloadButton;
    }

    /**
     * 페이지 입력 필드를 가져온다.
     *
     * @returns 페이지 입력 필드
     */
    getPageInput(): Number {
        if (this.pageInput === null) {
            this.pageInput = new Number({
                value: 1,
                minValue: 1,
                defaultValue: 1,
                width: 50,
                format: true,
                hidden: this.paginationType === 'simple',
                listeners: {
                    keyboard: async (e, me) => {
                        if (e.key === 'Enter') {
                            const value: number = await me.getValue();
                            await this.getStore().loadPage(value);
                        }
                    },
                },
            });
        }

        return this.pageInput;
    }

    /**
     * 마지막 페이지 뷰 필드를 가져온다.
     *
     * @returns 마지막 페이지 뷰 필드
     */
    getPageDisplay(): Display {
        if (this.pageDisplay === null) {
            if (this.paginationType === 'simple') {
                this.pageDisplay = new Display({
                    value: '1',
                    flex: 1,
                    textAlign: 'center',
                    textWrap: false,
                });
            } else {
                this.pageDisplay = new Display({
                    value: '1',
                    textWrap: false,
                    renderer: async (value) => {
                        const page = await Language.get('components.pagination.page').getText();

                        return `/ ${Format.number(value).format()} ${page}`;
                    },
                });
            }
        }

        return this.pageDisplay;
    }

    /**
     * 페이지를 이동한다.
     *
     * @param position - 이동할위치
     */
    async movePage(position: 'FIRST' | 'PREV' | 'NEXT' | 'LAST'): Promise<void> {
        if (this.getStore() == null) {
            return;
        }
        const provider = this.getStore().getProvider();
        const page = provider.getPage() ?? 1;
        const totalPage = provider.getTotalPage() ?? 1;
        let move = null;
        switch (position) {
            case 'FIRST':
                if (page > 1) {
                    move = 1;
                }
                break;
            case 'PREV':
                if (page > 1) {
                    move = page - 1;
                }
                break;
            case 'NEXT':
                if (page < totalPage) {
                    move = page + 1;
                }
                break;
            case 'LAST':
                if (page < totalPage) {
                    move = totalPage;
                }
                break;
        }
        if (move !== null) {
            await this.getStore().loadPage(move);
        }
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();
    }

    /**
     * 데이터스토어가 업데이트되었을 때 UI 를 업데이트한다.
     */
    #update(): void {
        const provider = this.getStore().getProvider();
        this.getFirstButton().setDisabled(provider.getPage() == 1);
        this.getPrevButton().setDisabled(provider.getPage() == 1);
        this.getNextButton().setDisabled(provider.getPage() == provider.getTotalPage());
        this.getLastButton().setDisabled(provider.getPage() == provider.getTotalPage());
        this.getPageInput().setValue(provider.getPage());
        this.getPageInput().setMaxValue(provider.getTotalPage());
        this.getPageDisplay().setValue(provider.getTotalPage());
    }
}
